import { ClaimCategory, SalesChannel } from '@pulse/constants/claims';
import ClaimService from '@pulse/services/ClaimService';
import OrderService from '@pulse/services/OrderService';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const DEFAULT_WARRANTY_CLAIM = {
  customer: {
    emailAddress: null,
    phoneNumber: null,
  },
  dateOfPurchase: null,
  item: {},
  receiptImages: [],
  receiptRequired: true,
  shippingAddress: null,
  signature: null,
  warrantyPeriod: null,
};

export const useInStore = config => {
  if (isEmpty(config)) {
    throw new Error('Missing config. Pass `useRuntimeConfig()`');
  }

  return defineStore('pulseInStore', () => {
    const claim = ref(DEFAULT_WARRANTY_CLAIM);
    const orderService = new OrderService(config.public.apiBaseUrl);
    const service = new ClaimService(config.public.apiBaseUrl);

    function setClaim(claimData) {
      claim.value = claimData;
    }

    /**
     * setShippingAddress
     * updates the claim.shippingAddress with the provided address
     * @param {Object} address
     */
    function setShippingAddress(address) {
      claim.value.shippingAddress = address;
    }

    /**
     * setEmailAddress
     * updates the claim.customer.emailAddress with the provided email address
     * @param {Object} address
     */
    function setEmailAddress(emailAddress) {
      claim.value.customer.emailAddress = emailAddress;
    }

    /**
     * setWarrantyPeriod
     * sets the warranty peiod with the provided period
     * @param {Object} address
     */
    function setWarrantyPeriod(warrantyPeriod) {
      claim.value.warrantyPeriod = warrantyPeriod;
    }

    /**
     * setStore
     * sets the store the in store order is related to
     * @param {Object} address
     */
    function setStore(store) {
      claim.value.store = store;
    }

    /**
     * setReceiptImages
     * sets the receipt images uploaded
     * @param {Object} address
     */
    function setReceiptImages(images) {
      claim.value.receiptImages = images;
    }

    /**
     * setPhoneNumber
     * updates the claim.customer.phoneNumber with the provided phone number
     * @param {Object} address
     */
    function setPhoneNumber(phoneNumber) {
      claim.value.customer.phoneNumber = phoneNumber;
    }

    /**
     * setDateOfPurchase
     * updates the claim.dateOfPurchase with the provided date
     * @param {Object} address
     */
    function setDateOfPurchase(date) {
      claim.value.dateOfPurchase = date;
    }

    function addSignature(data) {
      claim.value.signature = data;
    }

    function transformStoreToInStoreWarrantyClaim() {
      return {
        claimCategory: ClaimCategory.WARRANTY,
        customer: {
          email: claim.value.customer.emailAddress,
          phoneNumber: claim.value.customer.phoneNumber,
        },
        issueType: claim.value.item.issueType,
        item: {
          details: claim.value.item.details,
          images: claim.value.item.images,
          name: claim.value.item.item.name,
          productImage: claim.value.item.item.productImage,
          sku: claim.value.item.item.sku,
          sourceItemId: null,
          sourceProductId: claim.value.item.item.productId,
          sourceVariantId: claim.value.item.item.variantId,
        },
        salesChannel: SalesChannel.IN_STORE,
        shippingAddress: claim.value.shippingAddress,
        signature: claim.value.signature,
        storeId: claim.value.store.id,
        warranty: {
          dateOfPurchase: claim.value.dateOfPurchase,
          receiptImages: claim.value.receiptImages,
          receiptRequired: claim.value.receiptRequired,
          warrantyPeriod: claim.value.warrantyPeriod,
        },
      };
    }

    function submitClaim() {
      const transformed = transformStoreToInStoreWarrantyClaim();

      return service.createClaim({
        claim: transformed,
        sourceOrderId: storeOrderId.value,
      });
    }

    const storeOrderId = computed(() => {
      const randomLetters = () =>
        Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
          .toUpperCase()
          .slice(0, 4);

      const generateId = () => `${randomLetters()}-${randomLetters()}`;

      const initials = `${shippingAddress.value.firstName[0]}${shippingAddress.value.lastName[0]}`.toUpperCase();

      return `${initials}-${generateId()}`;
    });

    function setItem(item) {
      claim.value.item = item;
    }

    const receiptRequired = computed(() => {
      return claim.value.store.warrantySettings.receiptRequired;
    });

    const dateOfPurchase = computed(() => {
      return claim.value.dateOfPurchase;
    });

    const shippingAddress = computed(() => {
      return claim.value.shippingAddress;
    });

    const warrantyPeriod = computed(() => {
      return claim.value.warrantyPeriod;
    });

    const store = computed(() => {
      return claim.value.store;
    });

    const currentClaim = computed(() => {
      return claim.value;
    });

    function searchProducts(title, storeId) {
      return orderService.getProducts(title, storeId);
    }

    return {
      addSignature,
      currentClaim,
      dateOfPurchase,
      receiptRequired,
      searchProducts,
      setClaim,
      setDateOfPurchase,
      setEmailAddress,
      setItem,
      setPhoneNumber,
      setReceiptImages,
      setShippingAddress,
      setStore,
      setWarrantyPeriod,
      store,
      storeOrderId,
      submitClaim,
      warrantyPeriod,
    };
  });
};
